import React, { useState, useEffect, useMemo } from 'react';
import { getImageUrl } from '../utils/api';
import CampaignCountdown from './CampaignCountdown';

const CampaignCard = ({ campaign, onCampaignClick, campaignProducts = [], compact = false }) => {
    const [imageError, setImageError] = useState(false);
    const [currentRuleIndex, setCurrentRuleIndex] = useState(0);

    if (!campaign) return null;

    // Get campaign image or create collage from products
    const campaignImage = campaign.campaign_image || campaign.media_url;
    
    // For quantity_discount, get all valid rules with product names
    const allQuantityRules = useMemo(() => {
        if (campaign.campaign_type !== 'quantity_discount' || !campaign.quantity_rules || campaign.quantity_rules.length === 0) {
            return [];
        }
        
        // Filter valid rules (even if campaignProducts is empty, we can still show rules)
        const rules = campaign.quantity_rules
            .filter(rule => 
                rule.product_id && 
                (rule.unit_price || rule.total_price || (rule.buy_quantity && rule.get_quantity))
            )
            .map(rule => {
                // Try to find product name from campaignProducts
                const product = campaignProducts.find(p => p.id === rule.product_id);
                // If product not found, skip this rule (don't show "Ürün #X")
                if (!product || !product.name) {
                    return null;
                }
                return {
                    ...rule,
                    productName: product.name
                };
            })
            .filter(Boolean); // Remove null entries
        
        // Don't filter out rules without product names - show them anyway with product_id
        console.log('🔍 CampaignCard - allQuantityRules calculation:', {
            campaignId: campaign.id,
            hasQuantityRules: !!campaign.quantity_rules,
            quantityRulesLength: campaign.quantity_rules?.length || 0,
            campaignProductsLength: campaignProducts.length,
            filteredRulesLength: rules.length,
            rules: rules
        });
        
        return rules;
    }, [campaign.campaign_type, campaign.quantity_rules, campaignProducts]);
    
    // Rotate through rules every 2.5 seconds
    useEffect(() => {
        if (allQuantityRules.length <= 1) return;
        
        const interval = setInterval(() => {
            setCurrentRuleIndex((prev) => (prev + 1) % allQuantityRules.length);
        }, 2500);
        
        return () => clearInterval(interval);
    }, [allQuantityRules.length]);
    
    // Format discount info
    const getDiscountText = () => {
        if (campaign.campaign_type === 'nox_choice') {
            // Nox's Choice campaign - show discount from vote option
            const product = campaignProducts?.[0] || campaign.noxChoiceProduct;
            if (product && product.discount_price) {
                const originalPrice = parseFloat(product.price) || 0;
                const discountPrice = parseFloat(product.discount_price) || 0;
                if (originalPrice > discountPrice) {
                    const discountPercent = Math.round(((originalPrice - discountPrice) / originalPrice) * 100);
                    return `%${discountPercent} İndirim`;
                }
            }
            return '🏆 Sizin Seçiminiz';
        }
        if (campaign.campaign_type === 'discount') {
            const product = campaignProducts?.[0];
            if (product) {
                const originalPrice = parseFloat(product.price) || 0;
                let discountedPrice = originalPrice;
                if (campaign.discount_type === 'percentage') {
                    const discountValue = parseFloat(campaign.discount_value) || 0;
                    discountedPrice = originalPrice * (1 - discountValue / 100);
                } else if (campaign.discount_type === 'fixed_amount') {
                    const discountValue = parseFloat(campaign.discount_value) || 0;
                    discountedPrice = Math.max(0, originalPrice - discountValue);
                }
                if (product.discount_price) {
                    discountedPrice = parseFloat(product.discount_price);
                }
                if (originalPrice > discountedPrice && discountedPrice > 0) {
                    const pill = (price, strike, small) => (
                        <span
                            className={small ? 'px-1.5 py-0.5 text-[10px] font-bold rounded-full' : 'px-2 py-0.5 text-xs font-bold rounded-full'}
                            style={strike
                                ? { backgroundColor: '#fee2e2', color: '#dc2626', border: '1px solid #dc2626', textDecoration: 'line-through' }
                                : { backgroundColor: '#16a34a', color: '#ffffff' }}
                        >
                            {price.toFixed(0)}₺
                        </span>
                    );
                    return (
                        <span className="flex items-center gap-1 leading-none">
                            {pill(originalPrice, true, compact)}
                            {pill(discountedPrice, false, compact)}
                        </span>
                    );
                }
            }
            if (campaign.discount_type === 'percentage') {
                const value = parseFloat(campaign.discount_value);
                return `%${Math.round(value)} İndirim`;
            }
            return `${parseFloat(campaign.discount_value).toFixed(0)}₺ İndirim`;
        }
        if (campaign.campaign_type === 'combo' || campaign.campaign_type === 'combination') {
            const comboPrice = parseFloat(campaign.combo_price) || 0;
            
            // Calculate total original price from combination_rules or campaignProducts
            let totalOriginalPrice = 0;
            
            // First try: Calculate from combination_rules (most accurate)
            if (campaign.combination_rules && Array.isArray(campaign.combination_rules) && campaignProducts && campaignProducts.length > 0) {
                totalOriginalPrice = campaign.combination_rules.reduce((sum, rule) => {
                    const ruleProduct = campaignProducts.find(p => p.id === rule.product_id);
                    if (ruleProduct) {
                        const productPrice = parseFloat(ruleProduct.price) || 0;
                        const quantity = rule.min_quantity || 1;
                        return sum + (productPrice * quantity);
                    }
                    return sum;
                }, 0);
            }
            
            // Fallback: Calculate from campaignProducts if combination_rules not available
            if (totalOriginalPrice === 0 && campaignProducts && campaignProducts.length > 0) {
                totalOriginalPrice = campaignProducts.reduce((sum, product) => {
                    const productPrice = parseFloat(product.price) || 0;
                    const quantity = product.quantity || 1;
                    return sum + (productPrice * quantity);
                }, 0);
            }
            
            // If we have total original price and it's greater than combo price, show both
            if (totalOriginalPrice > 0 && totalOriginalPrice > comboPrice && comboPrice > 0) {
                return (
                    <span className="flex items-center gap-2">
                        <span 
                            className="px-2.5 py-1 text-sm font-bold rounded-full line-through"
                            style={{
                                backgroundColor: '#fee2e2',
                                color: '#dc2626',
                                border: '1px solid #dc2626'
                            }}
                        >
                            {totalOriginalPrice.toFixed(0)}₺
                        </span>
                        <span 
                            className="px-2.5 py-1 text-base font-bold rounded-full"
                            style={{
                                backgroundColor: '#16a34a',
                                color: '#ffffff'
                            }}
                        >
                            {comboPrice.toFixed(0)}₺
                        </span>
                    </span>
                );
            }
            
            return comboPrice > 0 ? `${comboPrice.toFixed(0)}₺` : 'Kampanya';
        }
        if (campaign.campaign_type === 'quantity_discount') {
            // Show rotating rules if available
            if (allQuantityRules.length > 0) {
                const currentRule = allQuantityRules[currentRuleIndex];
                const discountType = campaign.quantity_discount_type || 
                    (currentRule.unit_price ? 'unit_price' : 
                     currentRule.total_price ? 'total_price' : 
                     (currentRule.buy_quantity && currentRule.get_quantity) ? 'buy_x_get_y' : null);
                
                let totalPrice = null;
                let text = '';
                
                if (discountType === 'unit_price' && currentRule.unit_price) {
                    totalPrice = currentRule.min_quantity * parseFloat(currentRule.unit_price);
                    text = `${currentRule.min_quantity} Adet ${currentRule.productName} ${totalPrice.toFixed(0)} TL`;
                } else if (discountType === 'total_price' && currentRule.total_price) {
                    totalPrice = parseFloat(currentRule.total_price);
                    text = `${currentRule.min_quantity} Adet ${currentRule.productName} ${totalPrice.toFixed(0)} TL`;
                } else if (discountType === 'buy_x_get_y' && currentRule.buy_quantity && currentRule.get_quantity) {
                    if (currentRule.unit_price) {
                        totalPrice = currentRule.buy_quantity * parseFloat(currentRule.unit_price);
                        text = `${currentRule.buy_quantity}+${currentRule.get_quantity} ${currentRule.productName} ${totalPrice.toFixed(0)} TL`;
                    } else {
                        text = `${currentRule.buy_quantity}+${currentRule.get_quantity} ${currentRule.productName}`;
                    }
                }
                
                return text || 'Adet İndirimi';
            }
            return 'Adet İndirimi';
        }
        if (campaign.campaign_type === 'buy_x_get_y') {
            return `${campaign.buy_quantity}+${campaign.get_quantity}`;
        }
        return 'Kampanya';
    };

    // Compact/List view
    if (compact) {
        return (
            <div 
                onClick={onCampaignClick}
                className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden cursor-pointer group flex gap-4 p-3"
            >
                {/* Image - Small */}
                <div className="relative w-24 h-24 flex-shrink-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg overflow-hidden">
                    {campaignImage && !imageError ? (
                        <img
                            src={getImageUrl(campaignImage)}
                            alt={campaign.title}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            decoding="async"
                            onError={() => setImageError(true)}
                        />
                    ) : campaignProducts && campaignProducts.length > 0 ? (
                        campaign.campaign_type === 'discount' && campaignProducts.length === 1 ? (
                            (() => {
                                const product = campaignProducts[0];
                                const primaryImage = product.images?.find(img => img.is_primary) || product.images?.[0];
                                return primaryImage ? (
                                    <img
                                        src={getImageUrl(primaryImage.image_url)}
                                        alt={product.name}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                        decoding="async"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-gray-300">
                                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <rect x="3" y="3" width="18" height="18" rx="2" />
                                            <circle cx="8.5" cy="8.5" r="1.5" />
                                            <path d="M21 15l-5-5L5 21" />
                                        </svg>
                                    </div>
                                );
                            })()
                        ) : (
                            <div className="w-full h-full grid grid-cols-2">
                                {campaignProducts.slice(0, 4).map((product, idx) => {
                                    const primaryImage = product.images?.find(img => img.is_primary) || product.images?.[0];
                                    return (
                                        <div key={idx} className="relative overflow-hidden bg-gray-200">
                                            {primaryImage ? (
                                                <img
                                                    src={getImageUrl(primaryImage.image_url)}
                                                    alt={product.name}
                                                    className="w-full h-full object-cover"
                                                    loading="lazy"
                                                    decoding="async"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center bg-gray-300">
                                                    <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <rect x="3" y="3" width="18" height="18" rx="2" />
                                                        <circle cx="8.5" cy="8.5" r="1.5" />
                                                        <path d="M21 15l-5-5L5 21" />
                                                    </svg>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )
                    ) : (
                        <div className="w-full h-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <rect x="3" y="3" width="18" height="18" rx="2" />
                                <circle cx="8.5" cy="8.5" r="1.5" />
                                <path d="M21 15l-5-5L5 21" />
                            </svg>
                        </div>
                    )}
                    
                    {/* Discount Badge - Compact */}
                    {campaign.campaign_type !== 'combination' && campaign.campaign_type !== 'combo' && (
                        <div className={`absolute top-1 right-1 ${campaign.campaign_type === 'discount' && campaignProducts?.[0] ? 'bg-white/95 text-gray-900 px-1 py-0.5 rounded-md shadow' : 'bg-red-500 text-white px-1.5 py-0.5 rounded text-xs font-bold'}`}>
                            {(() => {
                                const text = getDiscountText();
                                return typeof text === 'string' ? text : text;
                            })()}
                        </div>
                    )}
                    
                    {/* Countdown Timer - Compact */}
                    {(campaign.end_date || campaign.end_datetime) && (
                        <div className="absolute bottom-1 left-1 bg-black/70 text-white px-1.5 py-0.5 rounded text-xs">
                            <CampaignCountdown endDate={campaign.end_date || campaign.end_datetime} />
                        </div>
                    )}
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0 flex flex-col justify-between">
                    <div>
                        <h3 className="font-bold text-base text-gray-900 line-clamp-1 mb-1">
                            {campaign.title}
                        </h3>
                        
                        {campaign.description && (
                            <p className="text-xs text-gray-600 line-clamp-1 mb-2">
                                {campaign.description}
                            </p>
                        )}
                        
                        {/* Campaign Type Badge */}
                        <div className="flex items-center gap-2 mb-2">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {campaign.campaign_type === 'nox_choice' && '🏆 Sizin Seçiminiz'}
                                {campaign.campaign_type === 'discount' && '💰 İndirim'}
                                {campaign.campaign_type === 'combo' && '🎁 Kombinasyon'}
                                {campaign.campaign_type === 'combination' && '🎯 Kombinasyon'}
                                {campaign.campaign_type === 'quantity_discount' && '📦 Adet İndirimi'}
                                {campaign.campaign_type === 'buy_x_get_y' && '➕ Al Get'}
                            </span>
                            
                            {campaignProducts && campaignProducts.length > 0 && (
                                <span className="text-xs text-gray-500">
                                    {campaignProducts.length} ürün
                                </span>
                            )}
                        </div>
                    </div>
                    
                    {/* Price Display - Compact */}
                    <div className="flex items-center gap-2 flex-wrap">
                        {/* Discount campaigns */}
                        {campaign.campaign_type === 'discount' && campaignProducts && campaignProducts.length > 0 && (() => {
                            const product = campaignProducts[0];
                            const originalPrice = parseFloat(product.price) || 0;
                            let discountedPrice = originalPrice;
                            
                            if (campaign.discount_type === 'percentage') {
                                const discountValue = parseFloat(campaign.discount_value) || 0;
                                discountedPrice = originalPrice * (1 - discountValue / 100);
                            } else if (campaign.discount_type === 'fixed_amount') {
                                const discountValue = parseFloat(campaign.discount_value) || 0;
                                discountedPrice = Math.max(0, originalPrice - discountValue);
                            }
                            
                            if (product.discount_price) {
                                discountedPrice = parseFloat(product.discount_price);
                            }
                            
                            if (originalPrice > discountedPrice && discountedPrice > 0) {
                                return (
                                    <>
                                        <span 
                                            className="px-2 py-0.5 text-xs font-bold rounded-full line-through"
                                            style={{
                                                backgroundColor: '#fee2e2',
                                                color: '#dc2626',
                                                border: '1px solid #dc2626'
                                            }}
                                        >
                                            {originalPrice.toFixed(0)}₺
                                        </span>
                                        <span 
                                            className="px-2 py-0.5 text-sm font-bold rounded-full"
                                            style={{
                                                backgroundColor: '#16a34a',
                                                color: '#ffffff'
                                            }}
                                        >
                                            {discountedPrice.toFixed(0)}₺
                                        </span>
                                    </>
                                );
                            }
                            return null;
                        })()}
                        
                        {/* Combination campaigns */}
                        {(campaign.campaign_type === 'combination' || campaign.campaign_type === 'combo') && campaign.combo_price && (() => {
                            const comboPrice = parseFloat(campaign.combo_price) || 0;
                            let totalOriginalPrice = 0;
                            
                            if (campaign.combination_rules && Array.isArray(campaign.combination_rules) && campaignProducts && campaignProducts.length > 0) {
                                totalOriginalPrice = campaign.combination_rules.reduce((sum, rule) => {
                                    const ruleProduct = campaignProducts.find(p => p.id === rule.product_id);
                                    if (ruleProduct) {
                                        const productPrice = parseFloat(ruleProduct.price) || 0;
                                        const quantity = rule.min_quantity || 1;
                                        return sum + (productPrice * quantity);
                                    }
                                    return sum;
                                }, 0);
                            }
                            
                            if (totalOriginalPrice === 0 && campaignProducts && campaignProducts.length > 0) {
                                totalOriginalPrice = campaignProducts.reduce((sum, product) => {
                                    const productPrice = parseFloat(product.price) || 0;
                                    const quantity = product.quantity || 1;
                                    return sum + (productPrice * quantity);
                                }, 0);
                            }
                            
                            if (totalOriginalPrice > 0 && totalOriginalPrice > comboPrice && comboPrice > 0) {
                                return (
                                    <>
                                        <span 
                                            className="px-2 py-0.5 text-xs font-bold rounded-full line-through"
                                            style={{
                                                backgroundColor: '#fee2e2',
                                                color: '#dc2626',
                                                border: '1px solid #dc2626'
                                            }}
                                        >
                                            {totalOriginalPrice.toFixed(0)}₺
                                        </span>
                                        <span 
                                            className="px-2 py-0.5 text-sm font-bold rounded-full"
                                            style={{
                                                backgroundColor: '#16a34a',
                                                color: '#ffffff'
                                            }}
                                        >
                                            {comboPrice.toFixed(0)}₺
                                        </span>
                                    </>
                                );
                            }
                            return null;
                        })()}
                        
                        {/* Quantity discount campaigns */}
                        {campaign.campaign_type === 'quantity_discount' && allQuantityRules.length > 0 && (() => {
                            const currentRule = allQuantityRules[currentRuleIndex];
                            const discountType = campaign.quantity_discount_type || 
                                (currentRule.unit_price ? 'unit_price' : 
                                 currentRule.total_price ? 'total_price' : 
                                 (currentRule.buy_quantity && currentRule.get_quantity) ? 'buy_x_get_y' : null);
                            
                            let totalPrice = null;
                            
                            if (discountType === 'unit_price' && currentRule.unit_price) {
                                totalPrice = currentRule.min_quantity * parseFloat(currentRule.unit_price);
                            } else if (discountType === 'total_price' && currentRule.total_price) {
                                totalPrice = parseFloat(currentRule.total_price);
                            } else if (discountType === 'buy_x_get_y' && currentRule.buy_quantity && currentRule.get_quantity && currentRule.unit_price) {
                                totalPrice = currentRule.buy_quantity * parseFloat(currentRule.unit_price);
                            }
                            
                            if (totalPrice) {
                                return (
                                    <span 
                                        className="px-2 py-0.5 text-sm font-bold rounded-full"
                                        style={{
                                            backgroundColor: '#16a34a',
                                            color: '#ffffff'
                                        }}
                                    >
                                        {totalPrice.toFixed(0)}₺
                                    </span>
                                );
                            }
                            return null;
                        })()}
                    </div>
                </div>
            </div>
        );
    }

    // Normal/Grid view
    return (
        <div 
            onClick={onCampaignClick}
            className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer group"
        >
            {/* Campaign Image / Collage */}
            <div className="relative w-full aspect-video bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
                {campaignImage && !imageError ? (
                    <img
                        src={getImageUrl(campaignImage)}
                        alt={campaign.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                        decoding="async"
                        onError={() => setImageError(true)}
                    />
                ) : campaignProducts && campaignProducts.length > 0 ? (
                    // For discount campaigns with single product, show product image full
                    campaign.campaign_type === 'discount' && campaignProducts.length === 1 ? (
                        <div className="w-full h-full relative overflow-hidden bg-gray-200">
                            {(() => {
                                const product = campaignProducts[0];
                                const primaryImage = product.images?.find(img => img.is_primary) || product.images?.[0];
                                return primaryImage ? (
                                    <img
                                        src={getImageUrl(primaryImage.image_url)}
                                        alt={product.name}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                        decoding="async"
                                        onError={(e) => {
                                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"%3E%3Crect x="3" y="3" width="18" height="18" rx="2"/%3E%3Ccircle cx="8.5" cy="8.5" r="1.5"/%3E%3Cpath d="M21 15l-5-5L5 21"/%3E%3C/svg%3E';
                                        }}
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-gray-300">
                                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <rect x="3" y="3" width="18" height="18" rx="2" />
                                            <circle cx="8.5" cy="8.5" r="1.5" />
                                            <path d="M21 15l-5-5L5 21" />
                                        </svg>
                                    </div>
                                );
                            })()}
                        </div>
                    ) : (
                        // Show product collage if multiple products or other campaign types
                        <div className="w-full h-full grid grid-cols-2">
                            {campaignProducts.slice(0, 4).map((product, idx) => {
                                const primaryImage = product.images?.find(img => img.is_primary) || product.images?.[0];
                                return (
                                    <div key={idx} className="relative overflow-hidden bg-gray-200">
                                        {primaryImage ? (
                                            <img
                                                src={getImageUrl(primaryImage.image_url)}
                                                alt={product.name}
                                                className="w-full h-full object-cover"
                                                loading="lazy"
                                                decoding="async"
                                                onError={(e) => {
                                                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"%3E%3Crect x="3" y="3" width="18" height="18" rx="2"/%3E%3Ccircle cx="8.5" cy="8.5" r="1.5"/%3E%3Cpath d="M21 15l-5-5L5 21"/%3E%3C/svg%3E';
                                                }}
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center bg-gray-300">
                                                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <rect x="3" y="3" width="18" height="18" rx="2" />
                                                    <circle cx="8.5" cy="8.5" r="1.5" />
                                                    <path d="M21 15l-5-5L5 21" />
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )
                ) : (
                    // Placeholder
                    <div className="w-full h-full flex items-center justify-center">
                        <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <rect x="3" y="3" width="18" height="18" rx="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <path d="M21 15l-5-5L5 21" />
                        </svg>
                    </div>
                )}

                {/* Discount Badge - Show for all campaigns except combination */}
                {campaign.campaign_type !== 'combination' && campaign.campaign_type !== 'combo' && (
                    <div className={`absolute top-3 right-3 shadow-lg ${
                        campaign.campaign_type === 'discount' && campaignProducts?.[0]
                            ? 'bg-white/95 text-gray-900 px-2 py-1 rounded-xl'
                            : 'bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold'
                    }`}>
                        {(() => {
                            const text = getDiscountText();
                            if (campaign.campaign_type === 'quantity_discount') {
                                console.log('🔍 CampaignCard - getDiscountText:', {
                                    campaignId: campaign.id,
                                    campaignType: campaign.campaign_type,
                                    allQuantityRulesLength: allQuantityRules.length,
                                    currentRuleIndex: currentRuleIndex,
                                    text: text
                                });
                            }
                            return typeof text === 'string' ? text : text;
                        })()}
                    </div>
                )}

                {/* Countdown Timer - Show for all campaigns with end_date */}
                {(campaign.end_date || campaign.end_datetime) && (
                    <div className="absolute top-3 left-3 bg-black/60 text-white px-2 py-1 rounded text-xs">
                        <CampaignCountdown endDate={campaign.end_date || campaign.end_datetime} />
                    </div>
                )}
            </div>

            {/* Campaign Info */}
            <div className="p-4">
                <h3 className="font-bold text-lg text-gray-900 line-clamp-2 mb-1">
                    {campaign.title}
                </h3>
                
                {campaign.description && (
                    <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                        {campaign.description}
                    </p>
                )}

                {/* Campaign Type Badge */}
                <div className="flex items-center gap-2 mb-3">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {campaign.campaign_type === 'nox_choice' && '🏆 Sizin Seçiminiz'}
                        {campaign.campaign_type === 'discount' && '💰 İndirim'}
                        {campaign.campaign_type === 'combo' && '🎁 Kombinasyon'}
                        {campaign.campaign_type === 'combination' && '🎯 Kombinasyon'}
                        {campaign.campaign_type === 'quantity_discount' && '📦 Adet İndirimi'}
                        {campaign.campaign_type === 'buy_x_get_y' && '➕ Al Get'}
                        {campaign.campaign_type === 'cross_product' && '🔗 Çapraz Ürün'}
                        {campaign.campaign_type === 'cross_category' && '🔗 Çapraz Kategori'}
                    </span>
                    
                    {campaignProducts && campaignProducts.length > 0 && (
                        <span className="text-xs text-gray-500">
                            {campaignProducts.length} ürün
                        </span>
                    )}
                </div>

                {/* Price Display - For discount campaigns */}
                {campaign.campaign_type === 'discount' && campaignProducts && campaignProducts.length > 0 && (() => {
                    const product = campaignProducts[0];
                    const originalPrice = parseFloat(product.price) || 0;
                    let discountedPrice = originalPrice;
                    
                    // Calculate discounted price from campaign
                    if (campaign.discount_type === 'percentage') {
                        const discountValue = parseFloat(campaign.discount_value) || 0;
                        discountedPrice = originalPrice * (1 - discountValue / 100);
                    } else if (campaign.discount_type === 'fixed_amount') {
                        const discountValue = parseFloat(campaign.discount_value) || 0;
                        discountedPrice = Math.max(0, originalPrice - discountValue);
                    }
                    
                    // Also check product's own discount_price if available
                    if (product.discount_price) {
                        discountedPrice = parseFloat(product.discount_price);
                    }
                    
                    if (originalPrice > discountedPrice && discountedPrice > 0) {
                        return (
                            <div className="flex items-center gap-2 mb-3">
                                <span 
                                    className="px-2.5 py-1 text-sm font-bold rounded-full line-through"
                                    style={{
                                        backgroundColor: '#fee2e2',
                                        color: '#dc2626',
                                        border: '1px solid #dc2626'
                                    }}
                                >
                                    {originalPrice.toFixed(2)} ₺
                                </span>
                                <span 
                                    className="px-2.5 py-1 text-base font-bold rounded-full"
                                    style={{
                                        backgroundColor: '#16a34a',
                                        color: '#ffffff'
                                    }}
                                >
                                    {discountedPrice.toFixed(2)} ₺
                                </span>
                            </div>
                        );
                    }
                    return null;
                })()}

                {/* Price Display - For combination campaigns */}
                {(campaign.campaign_type === 'combination' || campaign.campaign_type === 'combo') && campaign.combo_price && (() => {
                    const comboPrice = parseFloat(campaign.combo_price) || 0;
                    let totalOriginalPrice = 0;
                    
                    if (campaign.combination_rules && Array.isArray(campaign.combination_rules) && campaignProducts && campaignProducts.length > 0) {
                        totalOriginalPrice = campaign.combination_rules.reduce((sum, rule) => {
                            const ruleProduct = campaignProducts.find(p => p.id === rule.product_id);
                            if (ruleProduct) {
                                const productPrice = parseFloat(ruleProduct.price) || 0;
                                const quantity = rule.min_quantity || 1;
                                return sum + (productPrice * quantity);
                            }
                            return sum;
                        }, 0);
                    }
                    
                    if (totalOriginalPrice === 0 && campaignProducts && campaignProducts.length > 0) {
                        totalOriginalPrice = campaignProducts.reduce((sum, product) => {
                            const productPrice = parseFloat(product.price) || 0;
                            const quantity = product.quantity || 1;
                            return sum + (productPrice * quantity);
                        }, 0);
                    }
                    
                    if (totalOriginalPrice > 0 && totalOriginalPrice > comboPrice && comboPrice > 0) {
                        return (
                            <div className="flex items-center gap-2 mb-3">
                                {totalOriginalPrice && (
                                    <span 
                                        className="px-2.5 py-1 text-sm font-bold rounded-full line-through"
                                        style={{
                                            backgroundColor: '#fee2e2',
                                            color: '#dc2626',
                                            border: '1px solid #dc2626'
                                        }}
                                    >
                                        {totalOriginalPrice.toFixed(2)} ₺
                                    </span>
                                )}
                                <span 
                                    className="px-2.5 py-1 text-base font-bold rounded-full"
                                    style={{
                                        backgroundColor: '#16a34a',
                                        color: '#ffffff'
                                    }}
                                >
                                    {comboPrice.toFixed(2)} ₺
                                </span>
                            </div>
                        );
                    }
                    return null;
                })()}

                {/* Product Preview */}
                {campaignProducts && campaignProducts.length > 0 && (
                    <div className="flex gap-1 flex-wrap">
                        {campaignProducts.slice(0, 3).map((product) => (
                            <span
                                key={product.id}
                                className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                                title={product.name}
                            >
                                {product.name.substring(0, 15)}...
                            </span>
                        ))}
                        {campaignProducts.length > 3 && (
                            <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded font-semibold">
                                +{campaignProducts.length - 3}
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CampaignCard;

